import React, { ReactNode, createContext, useContext, useState, useEffect } from 'react';

import {
  getOrgByBusinessId,
  getSfaBusinessIdFromUrl,
  TransformedAuth0User,
  useIntegrations,
  useSelectCurrentUser
} from '@futrli/shared-services';
import { Integration } from '@futrli-sdk/app-store';
import { Organisation, OrganisationsForSageLinking } from '@futrli-sdk/organisations';
import { OrganisationListItem, WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { SFA_FATAL_ERROR, SFA_LINK_OPTIONS } from '../sfa-types';
import { useLoadWorkspacesData } from '../hooks/use-load-workspaces-data';
import { useLoadOrganisationsForLinking } from '../hooks/use-load-organisations-for-linking';

interface SfaOnboardingContextProps {
  children?: ReactNode;
}

interface SfaOnboardingContextValues {
  businessId: string | null;
  currentUser?: TransformedAuth0User;
  error?: SFA_FATAL_ERROR;
  finishedOrganisation?: Organisation | OrganisationListItem;
  integrations: Integration[];
  isLoading: boolean;
  linkingData?: OrganisationsForSageLinking;
  organisation?: OrganisationListItem;
  orgIdToLink?: string | SFA_LINK_OPTIONS.NEW;
  setError: (type: SFA_FATAL_ERROR | undefined) => void;
  setFinishedOrganisation: (org: Organisation | OrganisationListItem) => void;
  setOrgIdToLink: (id: string | SFA_LINK_OPTIONS.NEW | undefined) => void;
  workspace?: WorkspacesListingItem;
}

const defaultState = ({} as unknown) as SfaOnboardingContextValues;
export const SfaOnboardingContext = createContext(defaultState);

export const SfaOnboardingContextProvider = ({ children }: SfaOnboardingContextProps) => {
  const businessId = getSfaBusinessIdFromUrl();
  const currentUser = useSelectCurrentUser();
  const { data: workspacesData, isLoading: workspacesLoading } = useLoadWorkspacesData();
  const { workspace, organisation } = getOrgByBusinessId(workspacesData, businessId || '');
  const { integrations, isLoading: integrationsLoading } = useIntegrations();
  const { data: linkingData, isLoading: linkingDataLoading } = useLoadOrganisationsForLinking(
    workspace?.id,
    organisation && organisation.is_holding ? organisation.id : undefined
  );

  const [error, setError] = useState<SFA_FATAL_ERROR | undefined>();
  const [finishedOrganisation, setFinishedOrganisation] = useState<Organisation | OrganisationListItem>();
  const [orgIdToLink, setOrgIdToLink] = useState<string>();

  // Set any errors related to missing data here. Logic related errors (insufficient permissions etc) are
  // set in sfa-onboarding-container
  useEffect(() => {
    if (!businessId) {
      setError(SFA_FATAL_ERROR.BUSINESS_ID_MISSING);
    } else if (workspacesData && workspacesData.length === 0) {
      setError(SFA_FATAL_ERROR.NO_WORKSPACES);
    } else if (workspacesData && !organisation) {
      setError(SFA_FATAL_ERROR.BUSINESS_NOT_FOUND);
    }
  }, [businessId, organisation, workspacesData]);

  const values = {
    businessId,
    currentUser,
    error,
    finishedOrganisation,
    integrations,
    isLoading: integrationsLoading || linkingDataLoading || workspacesLoading,
    linkingData,
    organisation,
    orgIdToLink,
    setError,
    setFinishedOrganisation,
    setOrgIdToLink,
    workspace
  };

  return <SfaOnboardingContext.Provider value={values}>{children}</SfaOnboardingContext.Provider>;
};

export const useSfaOnboardingContext = () => useContext(SfaOnboardingContext);
